import { Plus } from "lucide-react";
import { AppButton } from "@libs/zapi-ui/custom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import { EmptyDimensionsState } from "./_EmptyDimensionsState";
import { SortableDimensionRow } from "./_SortableDimensionRow";
import { DimensionRow } from "./_DimensionRow";
import { useTranslation } from "react-i18next";

type TProps = {
  dimensions: TOrganizationProjectDimension[];
  confirmingDeleteId: number | null;
  deletingId: number | null;
  isDeletePending: boolean;
  onDragEnd: (event: DragEndEvent) => void;
  onEdit: (d: TOrganizationProjectDimension) => void;
  onAskDelete: (id: number) => void;
  onConfirmDelete: (id: number) => void;
  onCancelDelete: () => void;
  onAddNew: () => void;
};

export function DimensionsListView(props: TProps) {
  const {
    dimensions,
    confirmingDeleteId,
    deletingId,
    isDeletePending,
    onDragEnd,
    onEdit,
    onAskDelete,
    onConfirmDelete,
    onCancelDelete,
    onAddNew
  } = props;

  const { t } = useTranslation("projects");
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto" style={{ maxHeight: 360 }}>
        {dimensions.length === 0 ? (
          <EmptyDimensionsState />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} autoScroll={false}>
            <SortableContext items={dimensions.map((d) => d.id)} strategy={verticalListSortingStrategy}>
              <div className="flex flex-col gap-2 p-5">
                {dimensions.map((d) => {
                  const isConfirming = confirmingDeleteId === d.id;
                  const isThisDeleting = deletingId === d.id;
                  const otherDeleting = isDeletePending && !isThisDeleting;
                  return (
                    <SortableDimensionRow key={d.id} id={d.id} disabled={isConfirming || isDeletePending}>
                      <DimensionRow
                        dimension={d}
                        isConfirming={isConfirming}
                        isThisDeleting={isThisDeleting}
                        otherDeleting={otherDeleting}
                        onEdit={() => onEdit(d)}
                        onAskDelete={() => onAskDelete(d.id)}
                        onConfirmDelete={() => onConfirmDelete(d.id)}
                        onCancelDelete={onCancelDelete}
                      />
                    </SortableDimensionRow>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
      <div className="flex items-center justify-end border-t border-[#f4f4f5] bg-white p-5">
        <AppButton onClick={onAddNew} disabled={isDeletePending} data-testid="manage-dimensions-modal-new">
          <Plus className="h-4 w-4" />
          {t("dimensions.newTitle")}
        </AppButton>
      </div>
    </>
  );
}
